<template>
  <div>
    <span
      v-if="missingFields"
      class="caption block text-left text-error mb-2"
      @click="handleToggle"
    >
      <v-icon color="error" class="caption">mdi-alert-circle</v-icon>
      {{ missingFields }}
    </span>
    <span v-else class="toggle caption block text-left" @click="handleToggle">
      <v-icon color="primary" class="caption">mdi-help-circle</v-icon>
      {{
        autocomplete
          ? "Need to enter address manually?"
          : "Find address using Google Autocomplete"
      }}
    </span>
    <v-fade-transition mode="out-in">
      <div v-if="autocomplete">
        <v-text-field
          ref="autocompleteInput"
          variant="outlined"
          append-inner-icon="mdi-google-maps"
          label="Address"
          placeholder="Address"
          :rules="[validAddress(address)]"
          :model-value="addressToDisplay"
        />
      </div>
      <div v-else>
        <v-text-field
          v-model="street"
          variant="outlined"
          label="Street"
          :rules="requiredRules"
        />
        <v-text-field
          v-model="city"
          variant="outlined"
          label="City"
          :rules="requiredRules"
        />
        <v-text-field
          v-model="state"
          variant="outlined"
          label="State"
          :rules="requiredRules"
        />
        <v-text-field
          v-model="zip"
          variant="outlined"
          label="Zip Code"
          :rules="requiredRules"
          :error-messages="addressValidationErrorMessage"
        />
      </div>
    </v-fade-transition>
    <v-text-field
      v-model="suite"
      variant="outlined"
      label="Suite or Apt #"
      hint="(Optional)"
      persistent-hint
    />
  </div>
</template>

<script>
import { nextTick } from "vue";

import ValidationRules from "@/mixins/validationRules";

export default {
  name: "AddressField",
  mixins: [ValidationRules],
  props: {
    value: {
      type: String,
      default: ""
    },
    currentAddress: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      autocomplete: true,
      street: "",
      suite: "",
      city: "",
      state: "",
      zip: "",
      address: this.currentAddress || {},
      missingFields: "",
      googleAutocomplete: null
    };
  },
  computed: {
    valid() {
      if (this.missingFields === "") return true;
      return this.street && this.city && this.state && this.zip;
    },
    addressToDisplay() {
      if (Object.keys(this.address).length) {
        return this.address.id
          ? `${this.address.street} ${this.address.city}, ${this.address.zip_code}`
          : `${this.address.name}, ${this.address.locality}, ${this.address.administrative_area_level_1} ${this.address.postal_code}`;
      }
      return "";
    }
  },
  watch: {
    address(newValue, oldValue) {
      if (newValue !== oldValue && newValue.postal_code) {
        this.setFields();
      }
    },
    zip(newValue, oldValue) {
      if (newValue !== oldValue && newValue.length > 4) {
        this.validateCoverageArea(newValue);
      }
    },
    autocomplete(val) {
      if (val) {
        nextTick(() => {
          this.initGoogleAutocomplete();
        });
      }
    }
  },
  mounted() {
    if (this.currentAddress) {
      this.$emit("validate-form");
      this.suite = this.address.unit ? this.address.unit : "";
    }
    if (this.autocomplete) {
      this.initGoogleAutocomplete();
    }
  },
  methods: {
    initGoogleAutocomplete() {
      if (!window.google || !window.google.maps) return;
      const inputEl = this.$refs.autocompleteInput?.$el?.querySelector("input");
      if (!inputEl) return;
      this.googleAutocomplete = new window.google.maps.places.Autocomplete(
        inputEl,
        { types: ["address"] }
      );
      this.googleAutocomplete.addListener("place_changed", () => {
        const place = this.googleAutocomplete.getPlace();
        if (place && place.address_components) {
          const components = {};
          place.address_components.forEach((c) => {
            c.types.forEach((t) => {
              components[t] = c.long_name;
            });
          });
          this.address = {
            name: place.formatted_address,
            street_number: components.street_number || "",
            route: components.route || "",
            locality: components.locality || "",
            administrative_area_level_1: components.administrative_area_level_1 || "",
            postal_code: components.postal_code || "",
            isGoogleAddress: true
          };
        }
      });
    },
    handleToggle() {
      this.autocomplete = !this.autocomplete;
    },
    handlePlaceChange(place) {
      if (place) {
        this.address = { ...place, isGoogleAddress: true };
      }
    },
    getAddress() {
      let address = { ...this.address, unit: this.suite };
      if (!this.autocomplete) {
        address = {
          street: this.street,
          unit: this.suite,
          locality: this.city,
          administrative_area_level_1: this.state,
          postal_code: this.zip
        };
      }
      return address;
    },
    setFields() {
      this.street =
        this.address.street_number && this.address.route
          ? `${this.address.street_number} ${this.address.route}`
          : "";
      this.city = this.address.locality;
      this.state = this.address.administrative_area_level_1;
      this.zip = this.address.postal_code;
      if (
        !this.address.street_number ||
        !this.address.route ||
        !this.address.locality ||
        !this.address.administrative_area_level_1 ||
        !this.address.postal_code
      ) {
        this.missingFields =
          "Autocomplete suggestion is missing required fields, please enter them manually.";
        // Auto switch on google places error
        this.autocomplete = false;
        nextTick(() => {
          this.$emit("validate-form");
        });
      } else {
        this.missingFields = "";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.toggle {
  cursor: pointer;
  color: var(--v-primary-base);
}
</style>
